import { gql, useQuery } from '@apollo/client';
import {
  Box,
  BoxProps,
  Button,
  Divider,
  IconButton,
  IconButtonProps,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import * as React from 'react';

import { ChevronDownIcon, MenuIcon } from '@/imports/ui/chakra/feather';
import { useCurrent } from '@/hooks/useCurrent';
import { useIntutoLearningNavVisible } from '@/hooks/useIntutoLearningNavVisible';
import { usePermissions } from '@/hooks/usePermissions';
import { MEMBER_MEETINGS_LIST_PATH } from '@/modules/meetings/common/constants';
import { useAlphabetSoup } from '@/modules/alphabet-soup';

import {
  HeaderNavigation_Query,
  HeaderNavigation_Query_navigation_items,
} from './graphql-types/HeaderNavigation_Query';

import { HeaderNavigationMenuLink } from './components/HeaderNavigationMenuLink';
import { HeaderNavigationTabLink } from './components/HeaderNavigationTabLink';

const QUERY = gql`
  query HeaderNavigation_Query($input: NavigationInput!) {
    navigation(input: $input) {
      items {
        ...HeaderNavigationTabLink_NavigationItem
      }
      metadata {
        version
        publishedVersion
      }
    }
  }
  ${HeaderNavigationTabLink.navigationItemFragment}
`;

const normalizeNavHref = (href: string | null | undefined) =>
  (href ?? '').replace(/\/+$/, '');

const isCommunityHeaderItem = (
  item: HeaderNavigation_Query_navigation_items
) => {
  const href = normalizeNavHref(item.href);
  return (
    href === '/community' || item.label?.trim().toLowerCase() === 'community'
  );
};

const isMeetingsHeaderItem = (
  item: HeaderNavigation_Query_navigation_items
) => {
  const href = normalizeNavHref(item.href);
  return (
    href === normalizeNavHref(MEMBER_MEETINGS_LIST_PATH) ||
    item.label?.trim().toLowerCase() === 'meetings'
  );
};

function mergeLearningIntoHeaderNavigationItems(
  items: HeaderNavigation_Query_navigation_items[],
  includeLearningTab: boolean
): HeaderNavigation_Query_navigation_items[] {
  if (!items.length || !includeLearningTab) {
    return items;
  }
  const alreadyHasLearning = items.some(
    (item) =>
      item.href === '/learning' ||
      item.label?.trim().toLowerCase() === 'learning'
  );
  if (alreadyHasLearning) {
    return items;
  }
  const learningItem: HeaderNavigation_Query_navigation_items = {
    __typename: 'NavigationItem',
    label: 'Learning',
    href: '/learning',
    isExternal: false,
    children: [],
  };
  const eventsIndex = items.findIndex((item) => {
    const href = item.href?.replace(/\/+$/, '') ?? '';
    if (href === '/events' || href.endsWith('/events')) {
      return true;
    }
    return item.label?.trim().toLowerCase() === 'events';
  });
  const next = [...items];
  if (eventsIndex >= 0) {
    next.splice(eventsIndex + 1, 0, learningItem);
  } else {
    next.push(learningItem);
  }
  return next;
}

/**
 * Member-side only: when Community is hidden from header settings/access,
 * show Meetings at the end of header nav for logged-in users (same header tab UI).
 */
function mergeMeetingsWhenCommunityHidden(
  items: HeaderNavigation_Query_navigation_items[],
  options: { isAuthenticated: boolean; isWebsiteBuilder: boolean }
): HeaderNavigation_Query_navigation_items[] {
  if (options.isWebsiteBuilder || !options.isAuthenticated) {
    return items;
  }
  if (items.some(isCommunityHeaderItem)) {
    return items;
  }
  if (items.some(isMeetingsHeaderItem)) {
    return items;
  }

  const meetingsItem: HeaderNavigation_Query_navigation_items = {
    __typename: 'NavigationItem',
    label: 'Meetings',
    href: MEMBER_MEETINGS_LIST_PATH,
    isExternal: false,
    children: [],
  };

  // Append at the end of header nav (after existing items).
  return [...items, meetingsItem];
}

function useMergedHeaderNavigationItems(): {
  items: HeaderNavigation_Query_navigation_items[];
  hasNavigation: boolean;
} {
  const { isWebsiteBuilder } = useAlphabetSoup();
  const current = useCurrent();
  const permissions = usePermissions();
  const intutoNav = useIntutoLearningNavVisible();
  const query = useQuery<HeaderNavigation_Query>(QUERY, {
    variables: {
      input: { isUnpublishedVersion: isWebsiteBuilder },
    },
  });
  const raw = query.data?.navigation.items;
  const includeLearningTab =
    !permissions.isLoading &&
    !intutoNav.isLoading &&
    permissions.canAccessEventsFeed &&
    intutoNav.isVisible;
  const isAuthenticated = Boolean(current.user?._id);

  const items = React.useMemo(() => {
    if (!raw) {
      return [];
    }
    const withLearning = mergeLearningIntoHeaderNavigationItems(
      raw,
      includeLearningTab
    );
    return mergeMeetingsWhenCommunityHidden(withLearning, {
      isAuthenticated: !current.loading && isAuthenticated,
      isWebsiteBuilder,
    });
  }, [
    raw,
    includeLearningTab,
    current.loading,
    isAuthenticated,
    isWebsiteBuilder,
  ]);

  return {
    items,
    hasNavigation: !!query.data?.navigation && items.length > 0,
  };
}

const isTablet = typeof window !== 'undefined' && window.innerWidth < 1025;

const MAX_VISIBLE_ITEMS = isTablet ? 4 : 6;

// New component to handle dropdown items with nested children
const SeeMoreDropdownItem: React.FC<{ item: any }> = ({ item }) => {
  if (item.children && item.children.length > 0) {
    // Item has dropdown - render as nested menu
    return (
      <Menu placement="right-start">
        <MenuButton
          as={MenuItem}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {item.label}
          <Box as="span" ml={2}>▶</Box>
        </MenuButton>
        <MenuList>
          {item.children.map((child: any, idx: number) => (
            <MenuItem
              key={idx + child.label}
              as="a"
              href={child.href || undefined}
            >
              {child.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    );
  }

  // Regular item without dropdown
  return (
    <MenuItem
      as="a"
      href={item.href || undefined}
    >
      {item.label}
    </MenuItem>
  );
};

export const HeaderNavigationTabs: React.FC<BoxProps> = (props) => {
  const { items, hasNavigation } = useMergedHeaderNavigationItems();

  if (!hasNavigation) {
    return null;
  }

  const visibleItems = items.slice(0, MAX_VISIBLE_ITEMS);
  const dropdownItems = items.slice(MAX_VISIBLE_ITEMS);
  const hasDropdown = dropdownItems.length > 0;

  const itemsWithChildren = dropdownItems.filter(
    item => item.children?.length
  );

const minWidth = itemsWithChildren.length <= 1 ? "280px" : "550px";

  return (
    <Box display="flex" alignItems="center" {...props}>
      {/* Display first 6 items */}
      {visibleItems.map((item, index) => (
        <HeaderNavigationTabLink key={index + item.label} item={item} />
      ))}

      {/* Show "See More" dropdown if more than 6 items */}
      {hasDropdown && (
        <Menu autoSelect={false} closeOnSelect={false} isLazy>
         
          <MenuButton
            as={Button}
            variant="ghost"
            size="sm"
            ml={2}
          >
            See More <ChevronDownIcon boxSize={5} />
          </MenuButton>
             
      <MenuList p={2} minWidth={minWidth} fontFamily="poppins">
    <Box display="grid" gap={2} width="full" gridTemplateColumns="2fr">
      {/* LEFT: Items WITH children (wraps into rows) */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(260px, max-content))"
        gap={2}
        alignItems="start"
        justifyContent="start"
        width="full"
      >
        {dropdownItems
          .filter(item => item.children?.length)
          .map(item => (
            <Box key={item.label} bg="bgPrimary.500" h="full" borderRadius="md" p={2}>
              {/* Parent title */}
              <MenuItem
                fontWeight="semibold"
                pointerEvents="none"
                _hover={{ bg: 'transparent' }}
                color="textPrimary.500"
                width="auto"
                // textDecoration="underline"
                border={"0"}
                isDisabled
                _disabled={{color: "textPrimary.500"}}
              >
                {item.label}
              </MenuItem>

              {/* Children */}
              {item.children!.map(child => (
                <MenuItem
                  key={child.label}
                  as="a"
                  href={child.href || undefined}
                  pl={6}
                  width="auto"
                  border={"0"}
                  _hover={{ bg: 'bgSecondary.500', color: 'textPrimary.500' }}
                >
                  {child.label}
                </MenuItem>
              ))}
              
            </Box>
            
          ))}
      </Box>
          {dropdownItems.some(item => !item.children?.length) && <>
            <Divider orientation="horizontal" height="auto" borderTopColor="storkPrimary.500" />
            </>
          }
      {/* RIGHT: Simple links (wraps like pills) */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(260px, max-content))"
        gap={2}
        alignItems="start"
        justifyContent="start"
        width="full"
      >
        {dropdownItems
          .filter(item => !item.children?.length)
          .map(item => (
            <MenuItem
              key={item.label}
              as="a"
              href={item.href || undefined}
              width="auto"
              _hover={{ bg: 'bgSecondary.500', color: 'textPrimary.500' }}
            >
              {item.label}
            </MenuItem>
          ))}
      </Box>
    </Box>
  </MenuList>



        </Menu>
      )}
    </Box>
  );
};

export const HeaderNavigationMenu: React.FC<IconButtonProps> = (props) => {
  const modal = useDisclosure();
  const { items, hasNavigation } = useMergedHeaderNavigationItems();

  if (!hasNavigation) {
    return null;
  }

  return (
    <>
      <IconButton
        icon={<MenuIcon />}
        variant="outline"
        colorScheme="gray"
        onClick={modal.onOpen}
        {...props}
      />

      <Modal
        isOpen={modal.isOpen}
        onClose={modal.onClose}
        size="full"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader></ModalHeader>
          <ModalBody p="0">
            <Stack textAlign="center" spacing="0">
              {items.map((item, index) => (
                <HeaderNavigationMenuLink
                  key={index + item.label}
                  item={item}
                />
              ))}
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};